import React, { useState } from "react";
import { translate } from "@kaltura/mediaspace-shared-utils";
import YouTubeImportForm from "./YouTubeImportForm";
import { YouTubeVideoData } from "./types";
import { ModalTitle, ModalActions, Button } from "@kaltura/ds-react-components";
import { YouTubeImportModalState } from "./types";
import { KalturaExternalMediaEntry } from "kaltura-typescript-client/api/types/KalturaExternalMediaEntry";
import { StyledImportModalContent, StyledActions, StyledActionButtons } from "./StyleComponents";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";

export interface YouTubeImportModalProps {
    open: boolean;
    onCancel?: () => void;
    onBack?: () => void;
    onImport: (youtubeEntry: KalturaExternalMediaEntry) => void;
    initialState?: YouTubeImportModalState;
    uiconfId?: string;
    showTitle?: boolean;
    className?: string;
}

/*
 * YouTubeImportModal is a modal dialog wrapper component for importing YouTube videos.
 * It provides the modal title and action buttons, while delegating the import logic to YouTubeImportForm.
 * The modal acts as a container that can be reused with different form implementations.
 */
export const YouTubeImportModal: React.FC<YouTubeImportModalProps> = ({
    onCancel,
    onBack,
    onImport,
    initialState,
    uiconfId,
    showTitle = true,
    className,
}) => {
    const rootClassName = "youtube-import-modal";
    const [formData, setFormData] = useState<{
        videoData: YouTubeVideoData | null;
        isLoading: boolean;
        isImporting: boolean;
    }>({
        videoData: null,
        isLoading: false,
        isImporting: false,
    });

    const formId = `${rootClassName}-form`; // Create unique form ID
    const { showToast } = useToastsContext();
    const handleError = (message: string) => {
        showToast({ severity: "error", message });
    };

    return (
        <>
            {showTitle && (
                <ModalTitle data-testid={rootClassName + "-main-title"}>{translate("Import from YouTube")}</ModalTitle>
            )}
            <StyledImportModalContent className={`${className ?? ""} ${rootClassName}-content`}>
                <YouTubeImportForm
                    onImport={onImport}
                    initialState={initialState}
                    uiconfId={uiconfId}
                    rootClassName={rootClassName}
                    onDataChange={setFormData}
                    formId={formId}
                    onError={handleError}
                />
            </StyledImportModalContent>
            <ModalActions className={`${rootClassName}-actions`}>
                <StyledActions hasBackButton={!!onBack}>
                    {!!onBack && (
                        <Button variant="borderless" color="secondary" onClick={() => onBack()}>
                            {translate("Back")}
                        </Button>
                    )}

                    <StyledActionButtons>
                        {!!onCancel && (
                            <Button
                                kuid={rootClassName + "-button-cancel"}
                                variant="borderless"
                                onClick={onCancel}
                                color={"secondary"}
                            >
                                {translate("Cancel")}
                            </Button>
                        )}
                        <Button
                            variant="pill"
                            type={"submit"}
                            form={formId}
                            disabled={!formData.videoData || formData.isLoading || formData.isImporting}
                            loading={formData.isImporting}
                            kuid={rootClassName + "-button-import"}
                        >
                            {translate("Import from YouTube")}
                        </Button>
                    </StyledActionButtons>
                </StyledActions>
            </ModalActions>
        </>
    );
};

export default YouTubeImportModal;
